import React from 'react';
import {useAppSelector} from 'app/store';
import {Button, CaretLeftIcon} from 'components';
import {FormRadio} from 'components/Basic/Form/V2';
import {
  selectProviderFeePercentage,
  selectProviderSetFeePercentageStatus,
} from 'features/Provider/providerSelectors';
import {selectUserProfile} from 'features/User';
import {TFunction} from 'i18next';
import {ProviderProfile, ProviderRole, SliceStatus} from 'interfaces';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {setPlatformFeePercentage} from '../../paymentActions';

import {FeeSlider} from './Slider/FeeSlider';

const MAX = 20;
const MIN = 0;
const feeOptions = (t: TFunction<'translation', undefined>) => [
  {
    label: t('platformFee.iPayTotalAmount', 'I pay the total fee amount'),
    value: MAX,
  },
  {
    label: t(
      'platformFee.myClientsPayTotalAmount',
      'My clients pay the total fee amount',
    ),
    value: MIN,
  },
  {
    label: t(
      'platformFee.weSplitEqually',
      'My clients and I split the fee equally',
    ),
    value: 10,
  },
  {label: t('platformFee.Custom Split', 'Custom Split'), value: -1},
];

export function PlatformFeeSettings({
  onCancel,
}: {
  onCancel: () => void;
}): React.ReactElement {
  const user = useAppSelector(selectUserProfile) as ProviderProfile;
  const saveStatus = useAppSelector(selectProviderSetFeePercentageStatus);
  const isSaving = saveStatus === SliceStatus.pending;
  const userPercentage = useAppSelector(selectProviderFeePercentage) ?? 20;
  const {t} = useTranslation();

  const isCustomFeePercentage =
    userPercentage && ![MAX, MIN, 10].includes(userPercentage);

  const {control, watch, handleSubmit} = useForm({
    defaultValues: {
      customFeePercentage: isCustomFeePercentage ? user.feePercentage : 5,
      feePercentage: isCustomFeePercentage ? -1 : userPercentage,
    },
  });
  const dispatch = useDispatch();

  const feeOption = watch('feePercentage');

  const handleSave = (data: any) => {
    const feePercentage =
      data.feePercentage > -1 ? data.feePercentage : data.customFeePercentage;
    if (user) {
      dispatch(
        setPlatformFeePercentage({
          feePercentage,
          role: user.role as ProviderRole,
        }),
      );
    }
  };

  return (
    <div className="flex flex-col">
      <header className="flex items-center h-20 lg:h-12 px-5 lg:px-7 lg:pb-4 border-b">
        <button
          onClick={onCancel}
          className="inline-flex sm:hidden items-center justify-left cursor-pointer w-8 h-12 rounded-full"
        >
          <CaretLeftIcon fillColor="#000" width={18} height={18} />
        </button>
        <h2 className="font-inter text-lg leading-5 font-medium">
          {t('providerProfileMenuItems.revenue.dp.platformFee.modalTitle')}
        </h2>
      </header>
      <div className="flex flex-col gap-6 lg:gap-5 px-5 lg:pl-5 pb-5">
        <div className="w-full">
          <FormRadio
            id="feePercentage"
            gridType={1}
            name="feePercentage"
            control={control}
            options={feeOptions(t)}
          />
        </div>
        {feeOption === -1 ? (
          <Controller
            control={control}
            name="customFeePercentage"
            render={({field: {onChange, value}}) => (
              <FeeSlider
                min={MIN}
                max={MAX}
                step={0.1}
                value={value}
                onChange={value => onChange(value)}
              />
            )}
          />
        ) : null}
      </div>
      <div className="w-full flex justify-end items-center gap-2.5 border-t px-5 lg:px-8 pt-5">
        <Button
          type="button"
          btnType="primary"
          className="w-auto min-w-max h-12 flex justify-center items-center shadow-sm px-6 py-0 m-0 font-inter leading-5"
          fontSize="base"
          fontWeight="medium"
          borderRadius="xl"
          onClick={onCancel}
          outline
        >
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleSubmit(handleSave)}
          disabled={isSaving}
          isSubmitting={isSaving}
          btnType="primary"
          className="w-auto min-w-[96px] h-12 flex justify-center items-center shadow-sm px-6 py-0 m-0 font-inter leading-5"
          fontSize="base"
          fontWeight="medium"
          borderRadius="xl"
          loaderType="Oval"
        >
          {t('save', 'Save')}
        </Button>
      </div>
    </div>
  );
}
